import { useMutation, useQuery } from '@tanstack/react-query'

import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'

export type ApplicationGateStatus = 'sent' | 'paid' | 'submitted'

export type { ApplicationSubStatus } from '../../utils/applicationSubStatus'
export { formatApplicationFee } from '../../utils/applicationFee'

export interface ApplicationGateResponse {
    application_status: ApplicationGateStatus
    application_fee_cents: number
    property_name: string
    prospect_name: string
    is_unlocked: boolean
    docuseal_signing_url: string | null
    requires_checkout: boolean
    message: string | null
}

export interface ApplicationGateCheckoutResponse {
    client_secret: string
    application_fee_cents: number
}

export const useApplicationGate = (
    token: string | undefined,
    options?: { pollUntilUnlocked?: boolean; sessionId?: string | null },
) => {
    const pollUntilUnlocked = options?.pollUntilUnlocked ?? false
    const sessionId = options?.sessionId ?? null

    const gateQuery = useQuery<AxiosResponse<ApplicationGateResponse>, Error>({
        queryKey: ['application-gate', token, sessionId],
        queryFn: () =>
            axiosInstance.get(`/outreach/application-gate/${token}/`, {
                params: sessionId ? { session_id: sessionId } : undefined,
            }),
        enabled: !!token,
        refetchInterval: (query) => {
            if (!pollUntilUnlocked) {
                return false
            }
            const response = query?.data as
                | AxiosResponse<ApplicationGateResponse>
                | undefined
            if (response?.data?.is_unlocked) {
                return false
            }
            return 2000
        },
    })

    const createCheckout = useMutation<
        AxiosResponse<ApplicationGateCheckoutResponse>,
        Error
    >({
        mutationFn: () =>
            axiosInstance.post(`/outreach/application-gate/${token}/checkout/`),
    })

    return {
        gate: gateQuery.data?.data,
        isLoading: gateQuery.isLoading,
        error: gateQuery.error,
        refetchGate: gateQuery.refetch,
        createCheckout,
    }
}
